import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Pencil, Plus, Users } from "lucide-react";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { AdminDrawerFooter } from "@/components/admin/AdminDrawerFooter";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import {
  adminDrawerBodyStyle,
  adminFieldStyle,
  adminInputStyle,
  adminLabelStyle,
} from "@/components/admin/adminFormStyles";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { getOperationalCapabilities } from "@/features/auth/operationalCapabilities";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { OperationalQueryState } from "@/components/operational/OperationalQueryState";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import {
  useClientPage,
  useCreateClient,
  useDeleteClient,
  useUpdateClient,
} from "@/features/client/hooks";
import type { Client } from "@/features/client/types";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  formatClientDocument,
  getClientDocumentLabel,
  getValidClientOffset,
  normalizeClientDocument,
} from "./clientesDisplay";

const CLIENT_PAGE_SIZE = 10;

type DrawerMode = "create" | "edit";

interface DrawerState {
  mode: DrawerMode;
  client?: Client;
}

type ClientQueryState = {
  offset: number;
  search: string;
};

function ClientLedgerSkeleton() {
  return (
    <div
      aria-label="Carregando clientes"
      className="operational-directory clientes-ledger clientes-ledger--skeleton"
      role="status"
    >
      <div className="clientes-ledger__skeleton-header" />
      {Array.from({ length: 5 }, (_, index) => (
        <div className="clientes-ledger__skeleton-row" key={index}>
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}

function ClientLedger({
  clients,
  isFetching,
  canEdit,
  onEdit,
}: {
  clients: Client[];
  isFetching: boolean;
  canEdit: boolean;
  onEdit: (client: Client) => void;
}) {
  return (
    <div
      aria-busy={isFetching}
      aria-label="Clientes"
      className="operational-directory operational-directory__updating clientes-ledger"
      role="table"
    >
      <div className="clientes-ledger__header clientes-ledger__row" role="row">
        <span role="columnheader">Cliente</span>
        <span role="columnheader">Documento</span>
        {canEdit ? (
          <span className="clientes-ledger__action-heading" role="columnheader">
            Editar
          </span>
        ) : null}
      </div>

      {clients.map((client) => {
        const documentLabel = getClientDocumentLabel(client.document);
        return (
          <div
            className="clientes-ledger__row clientes-ledger__data-row"
            key={client.id}
            role="row"
          >
            <span className="clientes-ledger__name" role="cell">
              {client.name}
            </span>
            <span
              className={`clientes-ledger__document${documentLabel ? "" : " clientes-ledger__document--empty"}`}
              role="cell"
            >
              {documentLabel}
            </span>
            {canEdit ? (
              <span className="clientes-ledger__action-cell" role="cell">
                <button
                  aria-label={`Editar cliente ${client.name}`}
                  className="clientes-ledger__edit"
                  onClick={() => onEdit(client)}
                  title="Editar cliente"
                  type="button"
                >
                  <Pencil aria-hidden="true" size={16} strokeWidth={2} />
                </button>
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function ClientesPage() {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const { canDelete, canManageCatalog } =
    getOperationalCapabilities(currentUser);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState<ClientQueryState>({
    offset: 0,
    search: "",
  });
  const [dismissedListError, setDismissedListError] = useState<unknown>(null);

  const [name, setName] = useState("");
  const [document, setDocument] = useState("");

  const debouncedSearch = useDebouncedValue(searchInput, 250).trim();
  const clientPage = useClientPage({
    limit: CLIENT_PAGE_SIZE,
    offset: query.offset,
    search: query.search,
  });
  const createMutation = useCreateClient();
  const updateMutation = useUpdateClient();
  const deleteMutation = useDeleteClient();

  const items = clientPage.data?.items ?? [];
  const total = clientPage.data?.total ?? 0;
  const hasRows = items.length > 0;
  const showRefetchError =
    hasRows && clientPage.isError && dismissedListError !== clientPage.error;

  useEffect(() => {
    setQuery((current) =>
      current.search === debouncedSearch
        ? current
        : { offset: 0, search: debouncedSearch },
    );
  }, [debouncedSearch]);

  useEffect(() => {
    if (!clientPage.data || clientPage.isPlaceholderData) return;
    const validOffset = getValidClientOffset(
      clientPage.data.total,
      query.offset,
      CLIENT_PAGE_SIZE,
    );
    if (validOffset === query.offset) return;
    setQuery((current) => ({ ...current, offset: validOffset }));
  }, [clientPage.data, clientPage.isPlaceholderData, query.offset]);

  useEffect(() => {
    if (!clientPage.isError) setDismissedListError(null);
  }, [clientPage.isError]);

  function openCreate() {
    if (!canManageCatalog) return;
    setName("");
    setDocument("");
    setConfirmDelete(false);
    setDrawer({ mode: "create" });
  }

  function openEdit(client: Client) {
    if (!canManageCatalog) return;
    setName(client.name);
    setDocument(formatClientDocument(client.document ?? ""));
    setConfirmDelete(false);
    setDrawer({ mode: "edit", client });
  }

  function closeDrawer() {
    setDrawer(null);
    setConfirmDelete(false);
  }

  async function handleSubmit() {
    if (!canManageCatalog || !name.trim()) return;

    const payload = {
      name: name.trim(),
      document: normalizeClientDocument(document) || undefined,
    };

    if (drawer?.mode === "create") {
      await createMutation.mutateAsync(payload);
    } else if (drawer?.mode === "edit" && drawer.client) {
      await updateMutation.mutateAsync({
        id: drawer.client.id,
        payload,
      });
    }

    closeDrawer();
  }

  async function handleDelete() {
    if (!canDelete || !drawer?.client) return;
    await deleteMutation.mutateAsync(drawer.client.id);
    closeDrawer();
  }

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <AdminPageShell
      action={
        canManageCatalog ? (
          <button type="button" onClick={openCreate} className="clientes-add">
            <Plus size={16} strokeWidth={2} />
            Adicionar
          </button>
        ) : null
      }
      breadcrumb={
        <AdminBreadcrumb
          items={[
            { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
            { label: "Clientes" },
          ]}
        />
      }
      icon={Users}
      size="standard"
      title="CLIENTES"
    >
      <style>{`
        .app-shell-main:has(.clientes-directory) {
          background: #fff;
        }

        .operational-page-shell:has(.clientes-directory),
        :root:has(.clientes-directory) .admin-drawer-panel {
          --login-bg: #fff;
          --login-surface: #fff;
          --login-text: #183f3b;
          --login-muted: #647b7c;
          --login-border: #dee7e8;
          --login-accent: #1d704b;
        }

        .operational-page-shell:has(.clientes-directory) {
          --app-page-gutter: clamp(24px, 5vw, 72px);
          --operational-control-radius: 10px;
          max-width: calc(1120px + 2 * var(--app-page-gutter));
          padding-inline: var(--app-page-gutter);
          padding-block: 32px 72px;
        }

        :root[data-theme="dark"] .app-shell-main:has(.clientes-directory) {
          background: #0e0f10;
        }

        :root[data-theme="dark"] .operational-page-shell:has(.clientes-directory),
        :root[data-theme="dark"]:has(.clientes-directory) .admin-drawer-panel {
          --login-bg: #202122;
          --login-surface: #202122;
          --login-text: #f2f0eb;
          --login-muted: #a6a9af;
          --login-border: #303332;
          --login-accent: #8db47a;
        }

        :root[data-theme="dark"] .operational-page-shell:has(.clientes-directory) {
          background: #0e0f10;
        }

        .operational-page-shell:has(.clientes-directory) h1 {
          font-size: clamp(1.65rem, 4vw, 2rem) !important;
          letter-spacing: 0.02em !important;
        }

        .clientes-add {
          display: inline-flex;
          min-height: 44px;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0 15px;
          border: 0;
          border-radius: 12px;
          background: #a0eb70;
          color: #183f3b;
          font: 700 14px 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background-color 160ms ease, transform 160ms ease;
        }

        .clientes-add:hover:not(:disabled) {
          background: #8cdb5e;
        }

        .clientes-add:active:not(:disabled) {
          transform: scale(0.98);
        }

        .clientes-add:disabled {
          cursor: not-allowed;
          opacity: 0.55;
        }

        .clientes-add:focus-visible,
        :root:has(.clientes-directory) .admin-drawer-panel button:focus-visible {
          outline: 2px solid var(--login-accent);
          outline-offset: 2px;
        }

        :root[data-theme="dark"] .clientes-add {
          background: #8db47a;
          color: #0e0f10;
        }

        :root[data-theme="dark"] .clientes-add:hover:not(:disabled) {
          background: #9cc58a;
        }

        .clientes-directory .operational-search-field input {
          border-color: var(--login-border);
        }

        .clientes-directory .operational-search-field input::placeholder,
        :root:has(.clientes-directory) .admin-drawer-panel input::placeholder {
          color: var(--login-muted);
          opacity: 1;
        }

        :root:has(.clientes-directory) .admin-drawer-panel input {
          min-height: 44px;
          border-radius: 10px !important;
          background: var(--login-surface);
        }

        :root:has(.clientes-directory) .admin-drawer-panel input:focus-visible {
          outline: 2px solid var(--login-accent) !important;
          outline-offset: 2px;
        }

        :root:has(.clientes-directory) .admin-drawer-panel label {
          color: var(--login-text) !important;
          font: 700 12px 'DM Sans', sans-serif !important;
          letter-spacing: 0.02em !important;
        }

        :root:has(.clientes-directory) .admin-drawer-panel {
          border-color: var(--login-border) !important;
          border-radius: 16px !important;
        }

        :root:has(.clientes-directory) .admin-drawer-panel button:not(.admin-drawer-close) {
          border-radius: 10px !important;
          font: 700 14px 'DM Sans', sans-serif !important;
          letter-spacing: 0 !important;
          cursor: pointer;
          transition: background-color 160ms ease, transform 160ms ease;
        }

        :root:has(.clientes-directory) .admin-drawer-panel button:not(.admin-drawer-close):active:not(:disabled) {
          transform: scale(0.98);
        }

        :root:has(.clientes-directory) .admin-drawer-panel button:disabled {
          cursor: not-allowed;
          opacity: 0.55;
        }

        :root:has(.clientes-directory) .clientes-drawer-primary {
          background: #a0eb70 !important;
          color: #183f3b !important;
        }

        :root[data-theme="dark"]:has(.clientes-directory) .clientes-drawer-primary {
          background: #8db47a !important;
          color: #0e0f10 !important;
        }

        :root:has(.clientes-directory) .clientes-drawer-primary:hover:not(:disabled) {
          background: #8cdb5e !important;
        }

        :root[data-theme="dark"]:has(.clientes-directory) .clientes-drawer-primary:hover:not(:disabled) {
          background: #9cc58a !important;
        }

        :root:has(.clientes-directory) .clientes-drawer-danger:hover:not(:disabled) {
          background: color-mix(in srgb, #b83232 9%, var(--login-surface)) !important;
          color: #b83232 !important;
        }

        :root:has(.clientes-directory) .admin-drawer-close {
          min-width: 44px;
          min-height: 44px;
          border: 0 !important;
          border-radius: 10px !important;
        }

        :root:has(.clientes-directory) .admin-drawer-close:hover:not(:disabled) {
          background: color-mix(in srgb, #b83232 10%, var(--login-surface)) !important;
          color: #b83232 !important;
        }

        :root[data-theme="dark"]:has(.clientes-directory) .admin-drawer-close:hover:not(:disabled),
        :root[data-theme="dark"]:has(.clientes-directory) .clientes-drawer-danger:hover:not(:disabled) {
          color: #f19b89 !important;
        }

        .clientes-directory {
          display: grid;
          gap: 0.75rem;
          min-width: 0;
          font-family: 'DM Sans', sans-serif;
        }

        .clientes-directory__toolbar {
          width: 100%;
        }

        .clientes-ledger {
          --clientes-row-padding: 0.7rem var(--operational-row-inset);
          overflow: hidden;
          border-left: 0;
          border-right: 0;
          border-radius: 0;
          box-shadow: none;
          font-family: 'DM Sans', sans-serif;
        }

        .clientes-ledger__row {
          display: grid;
          min-width: 0;
          grid-template-columns: minmax(12rem, 1.45fr) minmax(10rem, 0.75fr) 2.75rem;
          align-items: center;
          gap: 0.85rem;
          padding: var(--clientes-row-padding);
          border-bottom: 1px solid var(--login-border);
        }

        .clientes-ledger:not(:has(.clientes-ledger__action-heading)) .clientes-ledger__row {
          grid-template-columns: minmax(0, 1.45fr) minmax(0, 0.75fr);
        }

        .clientes-ledger__row:last-child {
          border-bottom: 0;
        }

        .clientes-ledger__header {
          min-height: 2.75rem;
          background: color-mix(in srgb, var(--login-text) 3%, var(--login-surface));
          color: var(--login-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }

        .clientes-ledger__data-row {
          min-height: 4rem;
          background: var(--login-surface);
          transition: background var(--operational-motion-hover) ease;
        }

        .clientes-ledger__data-row:hover {
          background: color-mix(in srgb, var(--login-accent) 3.5%, var(--login-surface));
        }

        .clientes-ledger__name,
        .clientes-ledger__document {
          min-width: 0;
          overflow-wrap: anywhere;
        }

        .clientes-ledger__name {
          color: var(--login-text);
          font-size: 0.98rem;
          font-weight: 700;
          line-height: 1.3;
        }

        .clientes-ledger__document {
          color: var(--login-muted);
          font-family: 'DM Mono', monospace;
          font-size: 0.8rem;
          line-height: 1.42;
        }

        .clientes-ledger__action-heading {
          overflow: hidden;
          text-align: center;
        }

        .clientes-ledger__action-cell {
          display: flex;
          min-width: 0;
          justify-content: flex-end;
        }

        .clientes-ledger__edit {
          display: inline-flex;
          width: 2.75rem;
          height: 2.75rem;
          align-items: center;
          justify-content: center;
          justify-self: end;
          padding: 0;
          border: 1px solid transparent;
          border-radius: var(--operational-corner-sm);
          background: transparent;
          color: var(--login-accent);
          cursor: pointer;
        }

        .clientes-ledger__edit:hover {
          background: color-mix(in srgb, var(--login-accent) 8%, transparent);
        }

        .clientes-ledger__edit:focus-visible {
          outline: 2px solid var(--login-accent);
          outline-offset: 2px;
        }

        .clientes-directory__empty {
          display: grid;
          min-height: 10rem;
          place-items: center;
          padding: 1.5rem;
          border-top: 1px solid var(--login-border);
          border-bottom: 1px solid var(--login-border);
          background: var(--login-surface);
          color: var(--login-muted);
          text-align: center;
        }

        .clientes-directory__empty p {
          margin: 0;
          font-size: 0.9rem;
        }

        .clientes-ledger--skeleton {
          padding: 0;
        }

        .clientes-ledger__skeleton-header {
          height: 2.75rem;
          border-bottom: 1px solid var(--login-border);
          background: color-mix(in srgb, var(--login-text) 3%, var(--login-surface));
        }

        .clientes-ledger__skeleton-row {
          display: grid;
          min-height: 4rem;
          grid-template-columns: 1.45fr 0.75fr;
          align-items: center;
          gap: 1rem;
          padding: var(--clientes-row-padding);
          border-bottom: 1px solid var(--login-border);
        }

        .clientes-ledger__skeleton-row:last-child {
          border-bottom: 0;
        }

        .clientes-ledger__skeleton-row span {
          height: 0.75rem;
          border-radius: 3px;
          background: color-mix(in srgb, var(--login-muted) 16%, transparent);
          animation: clientes-skeleton-pulse 1.2s ease-in-out infinite alternate;
        }

        .clientes-directory .operational-pager--numbered {
          border-color: var(--login-border);
          color: var(--login-text);
          font-family: 'DM Sans', sans-serif;
        }

        .clientes-directory .operational-pager--numbered button:focus-visible {
          outline: 2px solid var(--login-accent);
          outline-offset: 2px;
        }

        .clientes-directory .operational-pager--numbered button:hover:not(:disabled) {
          color: var(--login-accent) !important;
          background: color-mix(in srgb, var(--login-accent) 6%, transparent) !important;
        }

        .clientes-directory .operational-pager--numbered button:disabled {
          cursor: not-allowed !important;
        }

        @keyframes clientes-skeleton-pulse {
          to { opacity: 0.42; }
        }

        @media (max-width: 640px) {
          .operational-page-shell:has(.clientes-directory) {
            --app-page-gutter: 14px;
            padding-block: 20px 72px;
          }

          .clientes-ledger {
            --clientes-row-padding: 0.75rem var(--operational-row-inset);
          }

          .clientes-ledger__header {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
            clip: rect(0 0 0 0);
            clip-path: inset(50%);
            white-space: nowrap;
          }

          .clientes-ledger__data-row {
            min-height: 4rem;
            grid-template-columns: minmax(0, 1fr) 2.75rem;
            align-content: center;
            column-gap: 0.55rem;
            row-gap: 0;
          }

          .clientes-ledger:not(:has(.clientes-ledger__action-heading)) .clientes-ledger__data-row {
            grid-template-columns: minmax(0, 1fr);
          }

          .clientes-ledger__name,
          .clientes-ledger__document {
            grid-column: 1;
          }

          .clientes-ledger__name {
            font-size: 1rem;
          }

          .clientes-ledger__document {
            font-size: 0.78rem;
          }

          .clientes-ledger__document:not(:empty) {
            margin-top: 0.2rem;
          }

          .clientes-ledger__document:empty {
            min-height: 0;
            height: 0;
            line-height: 0;
          }

          .clientes-ledger__action-cell {
            grid-column: 2;
            grid-row: 1 / span 2;
          }

          .clientes-ledger__skeleton-row {
            min-height: 4rem;
            grid-template-columns: 1fr;
            gap: 0.45rem;
          }

          .clientes-directory .operational-pager--numbered .pager-controls {
            flex-wrap: wrap;
            justify-content: center;
          }

          :root:has(.clientes-directory) .admin-drawer-panel {
            border-radius: 16px 16px 0 0 !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .clientes-ledger__data-row,
          .clientes-ledger__skeleton-row span,
          .clientes-add,
          :root:has(.clientes-directory) .admin-drawer-panel button {
            transition: none;
            animation: none;
          }
        }
      `}</style>

      <main className="clientes-directory">
        <div className="clientes-directory__toolbar">
          <OperationalSearchField
            ariaLabel="Buscar clientes por nome"
            onChange={setSearchInput}
            placeholder="Buscar clientes por nome..."
            value={searchInput}
          />
        </div>

        <OperationalQueryState
          error={hasRows ? null : clientPage.error}
          hasData={hasRows}
          isFetching={clientPage.isFetching}
          isFiltered={Boolean(query.search)}
          isLoading={clientPage.isLoading}
          onRetry={() => void clientPage.refetch()}
          renderEmpty={({ filtered }) => (
            <div className="clientes-directory__empty">
              <p>
                {filtered
                  ? "Nenhum cliente encontrado para esta busca."
                  : "Nenhum cliente cadastrado."}
              </p>
            </div>
          )}
          renderSkeleton={ClientLedgerSkeleton}
        >
          <ClientLedger
            clients={items}
            isFetching={clientPage.isFetching}
            canEdit={canManageCatalog}
            onEdit={openEdit}
          />
          <OperationalPager
            ariaLabel="Paginação de clientes"
            isFetching={clientPage.isFetching}
            itemLabel="cliente"
            itemLabelPlural="clientes"
            limit={CLIENT_PAGE_SIZE}
            offset={query.offset}
            onPageChange={(page) =>
              setQuery((current) => ({
                ...current,
                offset: page * CLIENT_PAGE_SIZE,
              }))
            }
            totalItems={total}
            variant="numbered"
          />
        </OperationalQueryState>

        {canManageCatalog ? (
          <AdminDrawer
            onClose={closeDrawer}
            onSubmit={handleSubmit}
            open={drawer !== null}
            submitDisabled={isPending || !name.trim()}
            title={
              drawer?.mode === "create" ? "Novo Cliente" : "Editar Cliente"
            }
            visualTreatment="operational-sheet"
          >
            <div style={adminDrawerBodyStyle}>
              <div style={adminFieldStyle}>
                <label htmlFor="clientes-name" style={adminLabelStyle}>
                  Nome
                </label>
                <input
                  id="clientes-name"
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ex: Supermercado Central"
                  style={adminInputStyle}
                  value={name}
                />
              </div>

              <div style={adminFieldStyle}>
                <label htmlFor="clientes-document" style={adminLabelStyle}>
                  CNPJ/CPF (opcional)
                </label>
                <input
                  id="clientes-document"
                  inputMode="numeric"
                  onChange={(event) =>
                    setDocument(formatClientDocument(event.target.value))
                  }
                  placeholder="Somente se precisar"
                  style={adminInputStyle}
                  value={document}
                />
              </div>
            </div>

            <AdminDrawerFooter
              confirmDelete={confirmDelete}
              isPending={isPending}
              mode={drawer?.mode ?? "create"}
              primaryClassName="clientes-drawer-primary"
              dangerClassName="clientes-drawer-danger"
              onConfirmDelete={canDelete ? handleDelete : undefined}
              onRequestDelete={
                canDelete ? () => setConfirmDelete(true) : undefined
              }
              onSubmit={handleSubmit}
              submitDisabled={!name.trim()}
              submitLabel={drawer?.mode === "create" ? "Criar" : "Salvar"}
            />
          </AdminDrawer>
        ) : null}

        {showRefetchError ? (
          <FloatingToast
            actionLabel="Tentar novamente"
            message={getUserFacingErrorMessage(
              clientPage.error,
              "Tente novamente em alguns instantes.",
            )}
            onAction={() => {
              setDismissedListError(clientPage.error);
              void clientPage.refetch();
            }}
            onDismiss={() => setDismissedListError(clientPage.error)}
            title="Não foi possível atualizar os clientes"
            tone="error"
          />
        ) : null}
      </main>
    </AdminPageShell>
  );
}
